/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import {
  CloudWasteSettingCategorised,
  CloudWasteSettingStatus,
} from "~/generated/graphql";
import { SettingParameter, UpdateSettingParameter } from "./types";
import { getOpposingStatus } from "./utils";

export const validateValue = (min: number, max: number, value: number) => {
  return min <= value && value <= max;
};

function mapData<T extends { parameters: SettingParameter[] }>(
  data: T[]
): (Omit<T, "parameters"> & {
  parameters: (Omit<SettingParameter, "value"> & UpdateSettingParameter)[];
})[] {
  return data.map((item) => {
    return {
      ...item,
      parameters: item.parameters.map((parameter) => {
        return {
          ...parameter,
          value: `${parameter.value}`,
        };
      }),
    };
  });
}

export function mapCategories(data: CloudWasteSettingCategorised[]) {
  return data.map((category) => ({
    ...category,
    settings: mapData(category.settings),
  }));
}

export function mapSettingsStatus<
  T extends { status: CloudWasteSettingStatus; id: string },
>(settings: T[], itemId: string) {
  return settings.map((prevItem) => {
    if (prevItem.id === itemId) {
      return {
        ...prevItem,
        status: getOpposingStatus(prevItem.status),
      };
    }
    return prevItem;
  });
}

type SettingsParametersProps<T> = {
  settings: T[];
  itemId: string;
  parameterId: string;
  value: string;
};

export function mapSettingsParameters<
  T extends {
    parameters: (Omit<SettingParameter, "value"> & UpdateSettingParameter)[];
    id: string;
  },
>({ settings, itemId, parameterId, value }: SettingsParametersProps<T>) {
  return settings.map((prevItem) => {
    if (prevItem.id === itemId) {
      return {
        ...prevItem,
        parameters: prevItem.parameters.map((parameter) => {
          if (parameter.id === parameterId) {
            return {
              ...parameter,
              value,
            };
          }
          return parameter;
        }),
      };
    }
    return prevItem;
  });
}
